import { Alert, Button, Descriptions, Empty, Progress, Space, Table, Tag, Tooltip, Typography, theme } from "antd";
import type { ColumnsType } from "antd/es/table";
import { TbAlertTriangle, TbBuildingBank, TbInfoCircle, TbReceipt } from "react-icons/tb";
import { formatMoney } from "utils/functions";
import {
  BASIS_LABEL,
  BASIS_SHORT,
  END_REASON_LABEL,
  MonthRow,
  ON_TRACK_TOLERANCE,
  SOURCE_META,
  VERDICT_META,
  monthLabel,
  pctLabel,
  shortDate,
} from "./constants";
import type { ReconLoan, ReconModel } from "./hooks";

const { Text, Title, Paragraph } = Typography;

interface PanelProps {
  loan: ReconLoan;
  model: ReconModel;
  onUploadStatements?: () => void;
}

const PanelShell = ({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) => {
  const { token } = theme.useToken();
  return (
    <div style={{ padding: "20px 24px", height: "100%", overflowY: "auto" }}>
      <Title level={5} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
        {title}
      </Title>
      <Paragraph style={{ margin: "2px 0 18px", fontSize: 13, color: token.colorTextTertiary }}>{subtitle}</Paragraph>
      {children}
    </div>
  );
};

const Figure = ({
  label,
  value,
  hint,
  dimmed,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  dimmed?: boolean;
  tone?: string;
}) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        background: token.colorFillQuaternary,
        borderRadius: token.borderRadiusLG,
        padding: "11px 13px",
        minWidth: 0,
      }}
    >
      <Text style={{ fontSize: 12, color: token.colorTextQuaternary, display: "block" }}>{label}</Text>
      <Text
        style={{
          display: "block",
          marginTop: 3,
          fontSize: 20,
          fontWeight: 500,
          fontVariantNumeric: "tabular-nums",
          color: dimmed ? token.colorTextQuaternary : (tone ?? token.colorText),
        }}
      >
        {value}
      </Text>
      {hint ? (
        <Text style={{ fontSize: 11, color: token.colorTextQuaternary, display: "block", marginTop: 2 }}>{hint}</Text>
      ) : null}
    </div>
  );
};

export const SummaryPanel = ({ loan, model, onUploadStatements }: PanelProps) => {
  const { token } = theme.useToken();
  const { result, verdict, expectedKnown, actualKnown, gapKnown, headline, pctFraction } = model;

  if (!result) {
    return (
      <PanelShell title="Summary" subtitle="Nothing has been calculated for this advance.">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Reconciliation needs a funded date, payback amount, installment amount and frequency before it can run."
        />
      </PanelShell>
    );
  }

  const meta = VERDICT_META[verdict];
  const { expected, actual, variance } = result;

  return (
    <PanelShell
      title="Summary"
      subtitle={`What ${pctLabel(pctFraction)} of receivables required, against what came in.`}
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Alert
          type={meta.alert}
          showIcon
          icon={meta.icon}
          message={<span style={{ fontWeight: 500 }}>{meta.title}</span>}
          description={<span style={{ fontSize: 13 }}>{headline}</span>}
        />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
          <Figure
            label="Percentage required"
            value={expectedKnown ? formatMoney(expected.total) : "—"}
            hint={expectedKnown ? "from revenue on file" : "no revenue on file"}
            dimmed={!expectedKnown}
          />
          <Figure
            label="Collected"
            value={actualKnown ? formatMoney(actual.total) : "—"}
            hint={actualKnown ? BASIS_SHORT[actual.basis].toLowerCase() : "assumed, not observed"}
            dimmed={!actualKnown}
          />
          <Figure
            label="Gap"
            value={gapKnown ? formatMoney(Math.abs(variance.amount)) : "—"}
            hint={gapKnown ? (variance.direction === "even" ? "level" : variance.direction) : "needs both sides"}
            dimmed={!gapKnown}
            tone={gapKnown && variance.direction === "underpaid" ? token.colorError : undefined}
          />
        </div>

        {gapKnown ? (
          <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
            Anything inside {pctLabel(ON_TRACK_TOLERANCE, 0)} of the required amount counts as on track.
          </Text>
        ) : (
          <div
            style={{
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: "13px 15px",
            }}
          >
            <Text style={{ display: "block", marginBottom: 10, fontSize: 13, fontWeight: 500 }}>
              To get a comparable number
            </Text>
            {!expectedKnown ? (
              <div style={{ display: "flex", gap: 9, marginBottom: 8 }}>
                <TbBuildingBank size={17} color={token.colorPrimary} style={{ flexShrink: 0, marginTop: 1 }} />
                <Text style={{ fontSize: 13 }}>
                  Link bank statements from {shortDate(loan.funded_date)} onward{" "}
                  <Text style={{ fontSize: 13, color: token.colorTextTertiary }}>— fixes the required side</Text>
                </Text>
              </div>
            ) : null}
            {!actualKnown ? (
              <div style={{ display: "flex", gap: 9 }}>
                <TbReceipt size={17} color={token.colorPrimary} style={{ flexShrink: 0, marginTop: 1 }} />
                <Text style={{ fontSize: 13 }}>
                  Feed the current balance and last payment date{" "}
                  <Text style={{ fontSize: 13, color: token.colorTextTertiary }}>
                    — replaces the assumption that every debit cleared
                  </Text>
                </Text>
              </div>
            ) : null}
            {!expectedKnown && onUploadStatements ? (
              <Button type="link" style={{ paddingInline: 0, marginTop: 6 }} onClick={onUploadStatements}>
                Upload statements
              </Button>
            ) : null}
          </div>
        )}

        {expected.cappedAtPayback ? (
          <Alert
            type="info"
            showIcon
            icon={<TbInfoCircle size={16} />}
            message={
              <span style={{ fontSize: 13 }}>
                The percentage reached full payback on {shortDate(expected.reconciledPayoffDate)}. Later months are
                excluded from the total.
              </span>
            }
          />
        ) : null}
      </Space>
    </PanelShell>
  );
};

export const EvidencePanel = ({ model }: PanelProps) => {
  const { token } = theme.useToken();
  const { result, actualKnown, installmentAmountSuspect } = model;

  if (!result) {
    return (
      <PanelShell title="Evidence" subtitle="Nothing to trace back.">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payload." />
      </PanelShell>
    );
  }

  const { coverage, actual, window } = result;

  return (
    <PanelShell title="Evidence" subtitle="Each side of the comparison, and how much of it was observed.">
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <Text style={{ fontSize: 13, fontWeight: 500 }}>Required amount</Text>
            <Text style={{ fontSize: 12, color: token.colorTextTertiary, fontVariantNumeric: "tabular-nums" }}>
              {coverage.monthsWithActualRevenue} of {coverage.monthsInWindow} months from statements
            </Text>
          </div>
          <Progress
            percent={Math.round(coverage.actualRevenueShare * 100)}
            size="small"
            strokeColor={token.colorPrimary}
            format={(p) => `${p}% real`}
          />
          <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
            {coverage.historyVisibleFromFundedDate
              ? "History reaches back to the funded date."
              : "History does not reach the funded date."}{" "}
            {coverage.continuous ? "No interior gaps." : "Coverage has interior gaps."}
          </Text>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <Text style={{ fontSize: 13, fontWeight: 500 }}>Amount collected</Text>
            <Tag color={actualKnown ? "green" : "red"} style={{ marginInlineEnd: 0 }}>
              {BASIS_SHORT[actual.basis]}
            </Tag>
          </div>
          <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>{BASIS_LABEL[actual.basis]}.</Text>
          <div style={{ marginTop: 6 }}>
            <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
              {installmentAmountSuspect
                ? `${window.installmentsElapsed} installments elapsed. The paid and missed counts are unusable — see data integrity.`
                : `${actual.installmentsPaid.toFixed(1)} of ${window.installmentsElapsed} installments accounted for, ${actual.installmentsMissed.toFixed(1)} missing.`}
            </Text>
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <Text style={{ fontSize: 13, fontWeight: 500 }}>Overall confidence</Text>
            <Space size={6}>
              <Tag
                color={coverage.confidence === "high" ? "green" : coverage.confidence === "medium" ? "gold" : "red"}
                style={{ marginInlineEnd: 0, textTransform: "capitalize" }}
              >
                {coverage.confidence}
              </Tag>
              <Tooltip title="High needs both a statement-backed revenue history and a balance-derived collections figure.">
                <span style={{ display: "inline-flex" }}>
                  <TbInfoCircle size={14} color={token.colorTextQuaternary} />
                </span>
              </Tooltip>
            </Space>
          </div>
          <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
            {coverage.confidence === "high"
              ? "Both sides come from observed data. Safe to act on."
              : coverage.confidence === "medium"
                ? "Directionally useful. Some months were estimated, so treat the exact figure loosely."
                : "Don't collect, escalate, or write off on this. Treat it as unreviewed."}
          </Text>
        </div>
      </Space>
    </PanelShell>
  );
};

export const TermsPanel = ({ loan, model }: PanelProps) => {
  const { result, pctFraction, expectedKnown, installmentAmountSuspect, installmentAmountUsed } = model;

  return (
    <PanelShell title="Terms and window" subtitle="The contract figures, and the period the calculation covered.">
      <Space direction="vertical" size={18} style={{ width: "100%" }}>
        <Descriptions
          column={1}
          size="small"
          bordered
          items={[
            { key: "funded", label: "Funded", children: formatMoney(loan.funded_amount) },
            { key: "payback", label: "Payback", children: formatMoney(loan.payback_amount) },
            { key: "balance", label: "Outstanding on record", children: formatMoney(loan.due_amount) },
            { key: "pct", label: "Specified share of receivables", children: pctLabel(pctFraction) },
            {
              key: "installment",
              label: "Installment used in the maths",
              children: installmentAmountUsed
                ? `${formatMoney(installmentAmountUsed)}${installmentAmountSuspect ? " (suspect)" : ""}`
                : "—",
            },
            {
              key: "implied",
              label: "Implied per installment",
              children:
                expectedKnown && result && !installmentAmountSuspect
                  ? formatMoney(result.expected.impliedWeeklyPayment)
                  : "—",
            },
          ]}
        />

        {result ? (
          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              { key: "funded_on", label: "Funded on", children: shortDate(result.window.fundedDate) },
              { key: "accrual", label: "Accrual starts", children: shortDate(result.window.accrualStart) },
              {
                key: "end",
                label: "Window closes",
                children: `${shortDate(result.window.end)} · ${END_REASON_LABEL[result.window.endReason]}`,
              },
              {
                key: "span",
                label: "Span",
                children: `${result.window.days} days · ${result.window.weeks.toFixed(1)} weeks`,
              },
              {
                key: "installments",
                label: "Installments elapsed",
                children: String(result.window.installmentsElapsed),
              },
            ]}
          />
        ) : null}
      </Space>
    </PanelShell>
  );
};

export const MonthsPanel = ({ model }: PanelProps) => {
  const { token } = theme.useToken();
  const { result } = model;
  const rows = result?.expected.breakdown ?? [];

  const columns: ColumnsType<MonthRow> = [
    {
      title: "Month",
      dataIndex: "month",
      render: (month: string) => <Text style={{ fontSize: 12 }}>{monthLabel(month)}</Text>,
    },
    {
      title: "Revenue",
      dataIndex: "monthlyRevenue",
      align: "right",
      render: (value: number, row) => (
        <Space size={6}>
          <Text
            style={{
              fontSize: 12,
              fontVariantNumeric: "tabular-nums",
              color: row.revenueSource === "estimated" ? token.colorTextQuaternary : undefined,
            }}
          >
            {formatMoney(value)}
          </Text>
          <Tooltip title={SOURCE_META[row.revenueSource].note}>
            <Tag color={SOURCE_META[row.revenueSource].color} style={{ marginInlineEnd: 0, fontSize: 10 }}>
              {SOURCE_META[row.revenueSource].label}
            </Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="Days of the month inside the window, and the fraction of a full month applied">Weight</Tooltip>
      ),
      dataIndex: "monthWeight",
      align: "right",
      render: (weight: number, row) => (
        <Text style={{ fontSize: 12, color: token.colorTextTertiary, fontVariantNumeric: "tabular-nums" }}>
          {row.daysInWindow}d · {weight.toFixed(2)}
        </Text>
      ),
    },
    {
      title: "Required",
      dataIndex: "expected",
      align: "right",
      render: (value: number) => (
        <Text style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{formatMoney(value)}</Text>
      ),
    },
    {
      title: "Cumulative",
      dataIndex: "cumulativeExpected",
      align: "right",
      render: (value: number) => (
        <Text style={{ fontSize: 12, color: token.colorTextTertiary, fontVariantNumeric: "tabular-nums" }}>
          {formatMoney(value)}
        </Text>
      ),
    },
  ];

  return (
    <PanelShell
      title="Month by month"
      subtitle="Every month the calculation touched, with where its revenue figure came from."
    >
      <Table<MonthRow>
        columns={columns}
        dataSource={rows}
        rowKey="month"
        size="small"
        pagination={false}
        scroll={{ y: 360 }}
        locale={{ emptyText: "The window produced no months." }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <Text style={{ fontSize: 12, fontWeight: 500 }}>{rows.length} months</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right" />
              <Table.Summary.Cell index={2} align="right" />
              <Table.Summary.Cell index={3} align="right">
                <Text style={{ fontSize: 12, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(result?.expected.total)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right" />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </PanelShell>
  );
};

export const IntegrityPanel = ({ model }: PanelProps) => {
  const { token } = theme.useToken();
  const { warnings } = model;

  if (warnings.length === 0) {
    return (
      <PanelShell title="Data integrity" subtitle="Nothing the calculation had to work around.">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Both sides came from observed data." />
      </PanelShell>
    );
  }

  return (
    <PanelShell title="Data integrity" subtitle="What the calculation had to assume, fill in, or work around.">
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        {warnings.map((warning) => {
          const color =
            warning.severity === "error"
              ? token.colorError
              : warning.severity === "warning"
                ? token.colorWarning
                : token.colorTextTertiary;
          return (
            <div
              key={warning.key}
              style={{
                display: "flex",
                gap: 10,
                padding: "11px 13px",
                border: `1px solid ${token.colorBorderSecondary}`,
                borderLeft: `3px solid ${color}`,
                borderRadius: token.borderRadiusLG,
              }}
            >
              {warning.severity === "info" ? (
                <TbInfoCircle size={16} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
              ) : (
                <TbAlertTriangle size={16} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
              )}
              <div style={{ minWidth: 0 }}>
                <Text style={{ display: "block", fontSize: 13, fontWeight: 500 }}>{warning.title}</Text>
                <Text style={{ display: "block", fontSize: 12, color: token.colorTextTertiary, marginTop: 2 }}>
                  {warning.body}
                </Text>
              </div>
            </div>
          );
        })}
      </Space>
    </PanelShell>
  );
};
